import { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  IconButton,
  Tabs,
  Tab,
  TextField,
  Divider,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Email,
  Phone,
  Business,
  LocationOn,
  Language,
  Instagram,
  LinkedIn,
  MoreVert,
  Delete,
  Send,
} from '@mui/icons-material';
import { useClient, useAddClientNote, useDeleteClientNote, useDeleteClient } from '../../hooks/useClients';
import { COLORS } from '@elevair/shared';
import EditClientModal from '../../components/clients/EditClientModal';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Box hidden={value !== index} pt={3}>
      {value === index && children}
    </Box>
  );
}

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isEditing = searchParams.get('edit') === 'true';

  const [tab, setTab] = useState(0);
  const [newNote, setNewNote] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [editModalOpen, setEditModalOpen] = useState(isEditing);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteNoteDialogOpen, setDeleteNoteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);

  const { data: client, isLoading, error } = useClient(id!);
  const addNote = useAddClientNote();
  const deleteNote = useDeleteClientNote();
  const deleteClient = useDeleteClient();

  const handleAddNote = async () => {
    if (!newNote.trim() || !id) return;

    await addNote.mutateAsync({ clientId: id, content: newNote });
    setNewNote('');
  };

  const handleOpenDeleteNoteDialog = (noteId: string) => {
    setNoteToDelete(noteId);
    setDeleteNoteDialogOpen(true);
  };

  const handleConfirmDeleteNote = async () => {
    if (!id || !noteToDelete) return;
    await deleteNote.mutateAsync({ clientId: id, noteId: noteToDelete });
    setDeleteNoteDialogOpen(false);
    setNoteToDelete(null);
  };

  const handleCloseDeleteNoteDialog = () => {
    setDeleteNoteDialogOpen(false);
    setNoteToDelete(null);
  };

  const handleArchive = async () => {
    if (!id) return;
    await deleteClient.mutateAsync({ id, permanent: false });
    navigate('/clients');
  };

  const handleOpenDeleteDialog = () => {
    setMenuAnchor(null);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!id) return;
    await deleteClient.mutateAsync({ id, permanent: true });
    setDeleteDialogOpen(false);
    navigate('/clients');
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !client) {
    return (
      <Alert severity="error">
        Client not found or failed to load.
        <Button onClick={() => navigate('/clients')} sx={{ ml: 2 }}>
          Back to Clients
        </Button>
      </Alert>
    );
  }

  const statusColors: Record<string, string> = {
    active: '#10B981',
    inactive: '#6B7280',
    lead: '#F59E0B',
    archived: '#EF4444',
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/clients')}>
          <ArrowBack />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h4" fontWeight={600}>
            {client.fullName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {client.jobTitle} {client.company && `at ${client.company}`}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Edit />}
          onClick={() => setEditModalOpen(true)}
        >
          Edit
        </Button>
        <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
          <MoreVert />
        </IconButton>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Client Info */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              {/* Avatar & Status */}
              <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                <Avatar
                  src={client.avatarUrl}
                  sx={{
                    width: 100,
                    height: 100,
                    bgcolor: client.avatarColor || COLORS.primary,
                    fontSize: '2rem',
                    mb: 2,
                  }}
                >
                  {client.initials}
                </Avatar>
                <Typography variant="h6">{client.fullName}</Typography>
                <Chip
                  label={client.status}
                  size="small"
                  sx={{
                    mt: 1,
                    bgcolor: `${statusColors[client.status]}20`,
                    color: statusColors[client.status],
                    fontWeight: 500,
                    textTransform: 'capitalize',
                  }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Contact Info */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Contact
                </Typography>

                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Email fontSize="small" color="action" />
                  <Typography variant="body2">{client.contact.email}</Typography>
                </Box>

                {client.contact.phone && (
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Phone fontSize="small" color="action" />
                    <Typography variant="body2">{client.contact.phone}</Typography>
                  </Box>
                )}

                {client.company && (
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Business fontSize="small" color="action" />
                    <Typography variant="body2">{client.company}</Typography>
                  </Box>
                )}

                {client.address?.city && (
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body2">
                      {[client.address.city, client.address.state].filter(Boolean).join(', ')}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Social Links */}
              {client.socialLinks && Object.values(client.socialLinks).some(Boolean) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Social
                    </Typography>
                    <Box display="flex" gap={1}>
                      {client.socialLinks.website && (
                        <IconButton
                          size="small"
                          href={client.socialLinks.website}
                          target="_blank"
                          component="a"
                        >
                          <Language />
                        </IconButton>
                      )}
                      {client.socialLinks.instagram && (
                        <IconButton
                          size="small"
                          href={`https://instagram.com/${client.socialLinks.instagram}`}
                          target="_blank"
                          component="a"
                        >
                          <Instagram />
                        </IconButton>
                      )}
                      {client.socialLinks.linkedin && (
                        <IconButton
                          size="small"
                          href={client.socialLinks.linkedin}
                          target="_blank"
                          component="a"
                        >
                          <LinkedIn />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </>
              )}

              {/* Tags */}
              {client.tags.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Tags
                    </Typography>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {client.tags.map((tag) => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                </>
              )}

              {/* Meta */}
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Added {new Date(client.createdAt).toLocaleDateString()}
                </Typography>
                {client.source && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Source: {client.source.replace('_', ' ')}
                    {client.sourceDetails && ` (${client.sourceDetails})`}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Tabs */}
        <Grid item xs={12} md={8}>
          <Card>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                <Tab label="Notes" />
                <Tab label="Bookings" />
                <Tab label="Invoices" />
                <Tab label="Projects" />
              </Tabs>
            </Box>

            <CardContent>
              {/* Notes Tab */}
              <TabPanel value={tab} index={0}>
                {/* Add Note */}
                <Box display="flex" gap={2} mb={3}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Add a note..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddNote}
                    disabled={!newNote.trim() || addNote.isPending}
                    sx={{ alignSelf: 'flex-end' }}
                  >
                    <Send />
                  </Button>
                </Box>

                {/* Notes List */}
                {client.notes && client.notes.length > 0 ? (
                  <Box display="flex" flexDirection="column" gap={2}>
                    {client.notes.map((note) => (
                      <Card key={note._id} variant="outlined">
                        <CardContent>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              {note.createdBy?.profile?.firstName} {note.createdBy?.profile?.lastName}
                              {' - '}
                              {new Date(note.createdAt).toLocaleString()}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDeleteNoteDialog(note._id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                          <Typography sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                            {note.content}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                ) : (
                  <Typography color="text.secondary" textAlign="center" py={4}>
                    No notes yet. Add your first note above.
                  </Typography>
                )}
              </TabPanel>

              {/* Bookings Tab */}
              <TabPanel value={tab} index={1}>
                <Typography color="text.secondary" textAlign="center" py={4}>
                  Bookings feature coming soon.
                </Typography>
              </TabPanel>

              {/* Invoices Tab */}
              <TabPanel value={tab} index={2}>
                <Typography color="text.secondary" textAlign="center" py={4}>
                  Invoices feature coming soon.
                </Typography>
              </TabPanel>

              {/* Projects Tab */}
              <TabPanel value={tab} index={3}>
                <Typography color="text.secondary" textAlign="center" py={4}>
                  Projects feature coming soon.
                </Typography>
              </TabPanel>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => { setMenuAnchor(null); window.location.href = `mailto:${client.contact.email}`; }}>
          Send Email
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); navigate('/bookings/new?client=' + id); }}>
          Create Booking
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); navigate('/invoices/new?client=' + id); }}>
          Create Invoice
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setMenuAnchor(null); handleArchive(); }}>
          Archive Client
        </MenuItem>
        <MenuItem onClick={handleOpenDeleteDialog} sx={{ color: 'error.main' }}>
          Delete Permanently
        </MenuItem>
      </Menu>

      {/* Edit Client Modal */}
      <EditClientModal
        open={editModalOpen}
        client={client}
        onClose={() => {
          setEditModalOpen(false);
          // Remove edit query param from URL
          if (searchParams.has('edit')) {
            searchParams.delete('edit');
            setSearchParams(searchParams);
          }
        }}
      />

      {/* Delete Client Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Client"
        message={
          <>
            Are you sure you want to permanently delete <strong>{client.fullName}</strong>?
            <br /><br />
            This will also delete all associated:
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Bookings and appointments</li>
              <li>Invoices and payments</li>
              <li>Contracts and documents</li>
              <li>Notes and communications</li>
              <li>Tasks and time entries</li>
            </ul>
            <strong>This action cannot be undone.</strong>
          </>
        }
        confirmText="Delete Client"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseDeleteDialog}
        isLoading={deleteClient.isPending}
      />

      {/* Delete Note Confirmation Dialog */}
      <ConfirmDialog
        open={deleteNoteDialogOpen}
        title="Delete Note"
        message="Are you sure you want to delete this note? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="warning"
        onConfirm={handleConfirmDeleteNote}
        onCancel={handleCloseDeleteNoteDialog}
        isLoading={deleteNote.isPending}
      />
    </Box>
  );
}
